import { ChevronLeftIcon } from '@langchain/untitled-ui-icons';

import { useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { Breadcrumb } from '@/components/Breadcrumbs';
import {
  useAnnotationQueues,
  useOrganizationId,
  useRun,
  useSession,
} from '@/hooks/useSwr';
import { appOrganizationPath, appSessionPath } from '@/utils/constants';
import { useGetGraphStudioPath } from '@/utils/useGetGraphStudioPath';

export function PlaygroundCrumb() {
  const { runId, annotationQueueId, sessionId, threadId } = useParams();
  const { data: run } = useRun({
    id: runId!,
    options: {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
    sessionId: sessionId,
  });
  const [searchParams] = useSearchParams();
  const threadIdSearchParam = searchParams.get('thread_id');
  const projectId = searchParams.get('project_id');
  const hostProjectId = searchParams.get('hostProjectId') ?? undefined;
  const { data: session } = useSession(sessionId ?? null);
  const organizationId = useOrganizationId();
  const annotationQueue = useAnnotationQueues(
    annotationQueueId ? { ids: [annotationQueueId] } : null
  );

  const { generatePath } = useGetGraphStudioPath();
  const { path, name } = useMemo(() => {
    if (annotationQueueId && sessionId) {
      return {
        path: `/${appOrganizationPath}/${organizationId}/annotation-queues/${annotationQueueId}/${appSessionPath}/${sessionId}?peek=${runId}`,
        name: `Back to ${session?.name ?? 'Project'}`,
      };
    }
    if (annotationQueueId) {
      return {
        path: `/${appOrganizationPath}/${organizationId}/annotation-queues/${annotationQueueId}?peekedRun=${runId}`,
        name: `Back to ${
          annotationQueue?.data?.[0]?.name ?? 'Annotation Queue'
        }`,
      };
    }
    if (threadId) {
      return {
        path: generatePath({ threadId, hostProjectId }),
        name: `Back to Studio`,
      };
    }
    // this is the case when we are coming from the thread tracing page
    if (threadIdSearchParam) {
      return {
        path: `/${appOrganizationPath}/${organizationId}/projects/p/${projectId}/t/${threadId}`,
        name: `Back to Thread ${threadId}`,
      };
    }
    return {
      path: run?.app_path ?? '',
      name: `Back to ${run?.name ?? 'Run'}`,
    };
  }, [
    run,
    annotationQueue,
    session,
    hostProjectId,
    threadId,
    projectId,
    organizationId,
    runId,
    annotationQueueId,
    sessionId,
    generatePath,
    threadIdSearchParam,
  ]);

  if (run == null) return null;

  return (
    <Breadcrumb
      path={path}
      name={name}
      icon={<ChevronLeftIcon className="size-4" />}
      isFirst
    />
  );
}
